/* A labelled distribution, drawn as CSS bars.

   The house choice, taken from the Grievance dashboard: a dozen labelled rows
   read better as bars in the flow of the page than as a chart.js canvas, and
   they stay legible in both themes without a theme-aware chart config. Reach
   for chart.js only when the shape of the data is the point — a trend over
   time, or a series that has to be compared point by point. */
import { cn } from "../../utils/tailwindUtils";

export const DistributionBars = ({
    rows = [],
    total,
    colorFor,
    emptyText = "Nothing to show yet.",
    labelWidth = "w-44",
    // The whole row, not just the count, reaches formatValue: a bar is sometimes
    // scaled by one figure and labelled with another (attendance scales by
    // present-%, but the number worth reading is the head count). Callers that
    // only want the count ignore the second argument.
    formatValue,
    valueWidth = "w-16",
}) => {
    if (!rows.length) {
        return <p className="text-sm text-muted-foreground">{emptyText}</p>;
    }

    // Fall back to the row total so a caller can hand over rows alone and still
    // get correct percentages.
    const denominator = total ?? rows.reduce((sum, row) => sum + (row.count || 0), 0);

    return (
        <div className="space-y-2">
            {rows.map((row) => {
                const pct = denominator ? Math.round((row.count * 100) / denominator) : 0;
                return (
                    <div key={row.key} className="flex items-center gap-3">
                        <span
                            className={cn(
                                "shrink-0 truncate text-xs text-gray-700 dark:text-gray-300",
                                labelWidth,
                            )}
                            title={row.label}
                        >
                            {row.label}
                        </span>
                        <div className="h-2.5 flex-1 overflow-hidden rounded-full bg-gray-100 dark:bg-gray-800">
                            {/* A non-zero count always paints at least a sliver, so a
                                real but tiny value never reads as nothing at all. */}
                            <div
                                className={cn("h-full rounded-full", colorFor ? colorFor(row) : "bg-sidebar-primary")}
                                style={{ width: `${Math.max(pct, row.count ? 2 : 0)}%` }}
                            />
                        </div>
                        <span
                            className={cn(
                                "shrink-0 text-right text-xs font-medium tabular-nums text-gray-700 dark:text-gray-300",
                                valueWidth,
                            )}
                        >
                            {formatValue ? formatValue(row.count, row) : row.count} · {pct}%
                        </span>
                    </div>
                );
            })}
        </div>
    );
};
